const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Share+Tech+Mono&family=VT323&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --black:   #030a03;
    --green:   #00ff41;
    --green2:  #00cc33;
    --green3:  #007a1f;
    --dim:     #004d14;
    --dimmer:  #002a0a;
    --red:     #ff3131;
    --amber:   #ffaa00;
    --font:    'Share Tech Mono', monospace;
    --display: 'VT323', monospace;
  }

  html, body { height: 100%; }

  body {
    font-family: var(--font);
    background: var(--black);
    color: var(--green);
    min-height: 100vh;
    /* CRT scan line texture */
    background-image: repeating-linear-gradient(
      0deg,
      transparent,
      transparent 2px,
      rgba(0, 255, 65, 0.015) 2px,
      rgba(0, 255, 65, 0.015) 4px
    );
  }

  /* Subtle screen flicker on load */
  @keyframes flicker {
    0%,100% { opacity: 1; }
    92%      { opacity: 1; }
    93%      { opacity: 0.85; }
    94%      { opacity: 1; }
    96%      { opacity: 0.9; }
    97%      { opacity: 1; }
  }

  .app {
    max-width: 860px;
    margin: 0 auto;
    padding: 2.5rem 1.5rem 4rem;
    animation: flicker 8s infinite;
  }

  /* ── HEADER ── */
  .header {
    border: 1px solid var(--green3);
    padding: 1.5rem 2rem;
    margin-bottom: 2rem;
    position: relative;
    overflow: hidden;
  }
  /* Corner brackets — purely decorative CSS */
  .header::before, .header::after {
    content: '';
    position: absolute;
    width: 12px; height: 12px;
    border-color: var(--green);
    border-style: solid;
  }
  .header::before { top: -1px; left: -1px; border-width: 2px 0 0 2px; }
  .header::after  { bottom: -1px; right: -1px; border-width: 0 2px 2px 0; }

  .header-top {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 0.5rem;
  }
  .header-top svg { color: var(--green2); }

  .header h1 {
    font-family: var(--display);
    font-size: 2.4rem;
    line-height: 1;
    color: var(--green);
    text-shadow: 0 0 20px rgba(0,255,65,0.5);
    letter-spacing: 2px;
  }
  .header-sub {
    font-size: 0.78rem;
    color: var(--green3);
    letter-spacing: 1px;
  }
  /* Blinking cursor at end of header */
  .blink {
    display: inline-block;
    width: 9px; height: 1.1em;
    background: var(--green);
    margin-left: 4px;
    vertical-align: middle;
    animation: blink 1s step-end infinite;
  }
  @keyframes blink { 50% { opacity: 0; } }

  /* ── SEARCH ── */
  .search-area {
    margin-bottom: 2rem;
  }
  .prompt-line {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.4rem;
    font-size: 0.75rem;
    color: var(--green3);
  }
  .prompt-sym { color: var(--green2); font-size: 1rem; }

  .search-row {
    display: flex;
    border: 1px solid var(--green3);
    background: var(--dimmer);
  }
  .search-row:focus-within { border-color: var(--green); box-shadow: 0 0 12px rgba(0,255,65,0.15); }

  /* The $ prompt prefix inside input */
  .input-prefix {
    padding: 0.85rem 0.75rem 0.85rem 1rem;
    color: var(--green2);
    font-size: 1rem;
    user-select: none;
  }

  .search-row input {
    flex: 1;
    background: none;
    border: none;
    outline: none;
    color: var(--green);
    font-family: var(--font);
    font-size: 1rem;
    padding: 0.85rem 0;
    caret-color: var(--green);
  }
  .search-row input::placeholder { color: var(--green3); }

  .search-row button {
    background: var(--dim);
    border: none;
    border-left: 1px solid var(--green3);
    color: var(--green);
    font-family: var(--font);
    font-size: 0.85rem;
    padding: 0 1.5rem;
    cursor: pointer;
    letter-spacing: 1px;
    text-transform: uppercase;
    transition: background 0.15s;
    display: flex; align-items: center; gap: 0.5rem;
  }
  .search-row button:hover { background: var(--green3); }
  .search-row button:disabled { opacity: 0.4; cursor: not-allowed; }

  /* ── LOG LINES (status messages) ── */
  .log {
    font-size: 0.78rem;
    margin-bottom: 1.5rem;
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
  }
  .log-line { display: flex; gap: 0.6rem; color: var(--green3); }
  .log-line.ok   .tag { color: var(--green2); }
  .log-line.err  .tag { color: var(--red); }
  .log-line.warn .tag { color: var(--amber); }
  .log-line .tag { min-width: 60px; }
  .log-line .msg { color: var(--green); }

  /* ── ERROR BOX ── */
  .error-box {
    border: 1px solid var(--red);
    padding: 1rem 1.25rem;
    color: var(--red);
    font-size: 0.85rem;
    margin-bottom: 1.5rem;
    background: rgba(255,49,49,0.05);
  }
  .error-box span { color: var(--red); opacity: 0.6; margin-right: 0.5rem; }

  /* ── PROFILE CARD ── */
  .profile-card {
    border: 1px solid var(--green3);
    animation: scanIn 0.4s ease both;
    position: relative;
  }

  @keyframes scanIn {
    from { opacity: 0; clip-path: inset(0 0 100% 0); }
    to   { opacity: 1; clip-path: inset(0 0 0% 0); }
  }

  /* Card title bar — like an old terminal window */
  .card-titlebar {
    background: var(--dim);
    border-bottom: 1px solid var(--green3);
    padding: 0.5rem 1rem;
    display: flex;
    align-items: center;
    gap: 1rem;
    font-size: 0.72rem;
    color: var(--green3);
  }
  .card-titlebar .dot {
    width: 8px; height: 8px;
    border-radius: 50%;
    background: var(--green3);
  }
  .card-titlebar .dot.active { background: var(--green); box-shadow: 0 0 6px var(--green); }

  /* Top section: avatar + main info */
  .profile-top {
    display: grid;
    grid-template-columns: auto 1fr;
    gap: 2rem;
    padding: 2rem;
    border-bottom: 1px solid var(--dimmer);
    align-items: start;
  }

  /* Avatar is rendered as a pixelated ASCII-ish bordered image */
  .avatar-wrap {
    position: relative;
  }
  .avatar-wrap::before {
    content: '';
    position: absolute;
    inset: -4px;
    border: 1px solid var(--green3);
    pointer-events: none;
  }
  .avatar-wrap img {
    width: 100px; height: 100px;
    display: block;
    filter: grayscale(100%) sepia(100%) hue-rotate(80deg) saturate(4) brightness(0.8);
    image-rendering: pixelated;
  }

  .profile-name {
    font-family: var(--display);
    font-size: 2rem;
    color: var(--green);
    text-shadow: 0 0 14px rgba(0,255,65,0.4);
    line-height: 1.1;
    letter-spacing: 1px;
  }
  .profile-login {
    font-size: 0.78rem;
    color: var(--green3);
    margin-bottom: 0.75rem;
  }
  .profile-login span { color: var(--green2); }
  .profile-bio {
    font-size: 0.85rem;
    color: var(--green2);
    line-height: 1.6;
    margin-bottom: 1rem;
    max-width: 480px;
  }

  /* Small meta tags: location, company, etc. */
  .profile-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 0.6rem;
  }
  .meta-item {
    display: flex;
    align-items: center;
    gap: 0.35rem;
    font-size: 0.75rem;
    color: var(--green3);
    border: 1px solid var(--green3);
    padding: 0.2rem 0.6rem;
  }
  .meta-item svg { color: var(--green2); flex-shrink: 0; }
  .meta-item a { color: var(--green3); text-decoration: none; }
  .meta-item a:hover { color: var(--green); }

  /* ── STAT ROW ── */
  .stat-row {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    border-bottom: 1px solid var(--dimmer);
  }
  .stat-cell {
    padding: 1.25rem 1.5rem;
    border-right: 1px solid var(--dimmer);
    display: flex; flex-direction: column; gap: 0.3rem;
  }
  .stat-cell:last-child { border-right: none; }
  .stat-label-row {
    display: flex; align-items: center; gap: 0.4rem;
    font-size: 0.7rem; color: var(--green3);
    text-transform: uppercase; letter-spacing: 1px;
  }
  .stat-label-row svg { color: var(--green3); }
  .stat-num {
    font-family: var(--display);
    font-size: 2rem;
    color: var(--green);
    text-shadow: 0 0 10px rgba(0,255,65,0.3);
    line-height: 1;
  }

  /* ── REPOS ── */
  .repos-section { padding: 1.5rem 2rem 2rem; }
  .repos-header {
    font-size: 0.72rem;
    color: var(--green3);
    text-transform: uppercase;
    letter-spacing: 2px;
    margin-bottom: 1rem;
    display: flex; align-items: center; gap: 0.5rem;
  }
  .repos-header::after {
    content: ''; flex: 1; height: 1px;
    background: var(--green3); opacity: 0.3;
  }

  .repos-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    gap: 0.75rem;
  }
  .repo-card {
    border: 1px solid var(--dim);
    padding: 1rem;
    background: var(--dimmer);
    transition: border-color 0.15s, background 0.15s;
    display: flex; flex-direction: column; gap: 0.4rem;
    text-decoration: none;
  }
  .repo-card:hover {
    border-color: var(--green2);
    background: var(--dim);
  }
  .repo-name {
    font-size: 0.88rem;
    color: var(--green);
    display: flex; align-items: center; gap: 0.4rem;
  }
  .repo-name svg { color: var(--green3); flex-shrink: 0; }
  .repo-desc { font-size: 0.75rem; color: var(--green3); line-height: 1.5; flex: 1; }
  .repo-footer {
    display: flex; gap: 1rem; margin-top: 0.4rem;
    font-size: 0.7rem; color: var(--green3);
  }
  .repo-footer span { display: flex; align-items: center; gap: 0.25rem; }
  .repo-footer svg { color: var(--amber); }

  /* Language dot */
  .lang-dot {
    width: 8px; height: 8px; border-radius: 50%;
    background: var(--green2);
    display: inline-block;
    box-shadow: 0 0 5px var(--green2);
    flex-shrink: 0;
  }

  /* ── IDLE STATE ── */
  .idle {
    border: 1px solid var(--green3);
    padding: 3rem 2rem;
    text-align: center;
    color: var(--green3);
  }
  .idle svg { margin-bottom: 1rem; opacity: 0.4; }
  .idle p { font-size: 0.85rem; line-height: 1.7; }
  .idle code {
    color: var(--green2);
    background: var(--dimmer);
    padding: 0.1rem 0.4rem;
  }

  /* ── SPINNER ── */
  .loading-lines {
    display: flex; flex-direction: column; gap: 0.3rem;
    font-size: 0.78rem; color: var(--green3);
    margin-bottom: 1.5rem;
  }
  @keyframes typeLine {
    from { width: 0; }
    to   { width: 100%; }
  }
  .type-line {
    overflow: hidden; white-space: nowrap;
    animation: typeLine 0.4s ease both;
    color: var(--green2);
  }
  .type-line:nth-child(1) { animation-delay: 0s; }
  .type-line:nth-child(2) { animation-delay: 0.25s; }
  .type-line:nth-child(3) { animation-delay: 0.5s; }

  /* ── RESPONSIVE ── */
  @media (max-width: 600px) {
    .profile-top { grid-template-columns: 1fr; }
    .stat-row { grid-template-columns: 1fr 1fr; }
    .stat-cell:nth-child(2) { border-right: none; }
    .repos-grid { grid-template-columns: 1fr; }
    .header h1 { font-size: 1.8rem; }
    .app { padding: 1.25rem 1rem 3rem; }
  }
`;

export default styles;
